/**
 * 10.9AH — ARENA DE DUELO 3D (PvP 1v1). Substitui o duelo abstrato 2D (canvas top-down) por uma
 * ARENA 3D REAL: os dois jogadores aparecem como Caçadores num anel 3D, você move no JOYSTICK e
 * o ataque é automático ao encostar (coerente com o jogo). Câmera 3/4. Auto-contido (cena/câmera/
 * renderer próprios, overlay) pra NÃO mexer no loop principal — a rede é a mesma (CloudDuel relay).
 * Mesma API pública da antiga DuelScreen (open/hide/onExit/onResult/onRematch), então o Game troca 1:1.
 */
import * as THREE from "three";
import { closeDuelChannel, openDuelChannel, sendDuelEnd, sendDuelHit, sendDuelState, type DuelState } from "../net/CloudDuel";
import type { LobbyPlayer } from "../net/CloudPvp";
import { buildWeaponMesh } from "../player/Player"; // 10.9AW — arma REAL da classe no avatar

const ARENA_R = 12;       // raio da arena (unidades)
const DUEL_HP = 100;
const MOVE_SPEED = 6.2;   // u/s
const ATTACK_RANGE = 3.2; // u
const ATTACK_CD = 0.55;   // s entre acertos

/** 10.9FG — magia levada pro duelo (as 3 do Grimorio) */
export interface PvpSpell {
  kind: string; name: string; emoji: string; color: number; cooldown: number; dmgMult: number; radius: number;
  buff?: { seconds: number; dmgMult?: number; dr?: number; atkSpeedMult?: number; critChance?: number; healFrac?: number };
  dashRange?: number; waveLength?: number; field?: { seconds: number; tickEvery: number }; meteorDelay?: number; meteorRange?: number;
  trap?: { seconds: number; trigger: number }; ahead?: number; drainPct?: number;
}
/** 10.9FG — atributos REAIS do Cacador no duelo (antes: 100 de vida fixa e dano pela razao de poder) */
export interface PvpStats {
  maxHp: number; dmg: number; aps: number; range: number; armor: number; dr: number; block: number;
  crit: number; critMult: number; lifeSteal: number; spells: PvpSpell[];
}
export interface DuelFighter { name: string; power: number; visualId?: string; energy?: number; stats?: PvpStats; weaponTier?: number; weaponFamily?: string } // 10.9HG: tier/linhagem → skin da arma

/** vida do duelo = vida do Cacador x isto (senao a luta acabava em 2 s) */
const PVP_HP_MULT = 16; // medido: x8 = ~6 s entre dois Nv 50; x16 = ~12 s
/** o golpe nunca e reduzido abaixo disto (anti-imortal, igual ao PvE) */
const PVP_MIN_TAKEN = 0.2;

interface Avatar {
  group: THREE.Group;
  body: THREE.Mesh;
  weapon: THREE.Object3D;
  slash: THREE.Mesh;
  mats: THREE.Material[];
}

export class DuelArena3D {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly statusEl: HTMLElement;
  private readonly meBar: HTMLElement;
  private readonly oppBar: HTMLElement;

  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(52, 1, 0.1, 200);
  private meAv!: Avatar;
  private oppAv!: Avatar;

  private myId = "";
  private oppName = "";
  private myCP = 0; // 10.9FP — Poder de Combate
  private oppCP = 0;
  private floatsEl!: HTMLElement;
  private myDmg = 6;
  private st: PvpStats | null = null; // 10.9FG
  private myMax = DUEL_HP;
  private atkCd = ATTACK_CD;
  private range = ATTACK_RANGE;
  private spellCd = [0, 0, 0];
  private buffT = 0;
  private buff: NonNullable<PvpSpell["buff"]> | null = null;
  private fields: { x: number; z: number; t: number; every: number; acc: number; r: number; dmg: number; follow: boolean; drain: number }[] = [];
  private traps: { x: number; z: number; t: number; trig: number; r: number; dmg: number }[] = [];
  private meteors: { x: number; z: number; t: number; r: number; dmg: number }[] = [];
  private fx: { m: THREE.Mesh; t: number; dur: number; grow: number }[] = [];
  private beam!: THREE.Mesh;
  private beamT = 0;
  private spellBtns: HTMLButtonElement[] = [];
  private me = { x: 0, z: 6, hp: DUEL_HP, f: Math.PI };
  private opp: DuelState = { x: 0, z: -6, hp: DUEL_HP, maxHp: DUEL_HP, f: 0 };
  private oppRender = new THREE.Vector3(0, 0, -6);
  private oppSeen = 0;
  private phase: "waiting" | "countdown" | "fighting" | "over" = "waiting";
  private countdown = 3;
  private waitTimer = 0;
  private moveDir = new THREE.Vector2(0, 0); // do joystick
  private atkTimer = 0;
  private netTimer = 0;
  private slashT = 0;
  private over = false;
  private raf = 0;
  private last = 0;

  constructor(
    uiRoot: HTMLElement,
    private readonly onExit: () => void,
    private readonly onResult: (won: boolean) => void,
    private readonly onRematch: (note?: string) => void,
  ) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay d3-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <canvas class="d3-canvas"></canvas>
      <div class="d3-hud">
        <div class="d3-top">
          <div class="d3-status"></div>
          <button class="btn d3-x" data-a="close">Sair</button>
        </div>
      </div>
      <div class="d3-bar d3-bar-me"><span class="d3-name"></span><span class="d3-cp"></span><span class="d3-hpwrap"><i></i></span></div>
      <div class="d3-bar d3-bar-opp"><span class="d3-name"></span><span class="d3-cp"></span><span class="d3-hpwrap"><i></i></span></div>
      <div class="d3-stick"><div class="d3-knob"></div></div>
      <div class="d3-spells"></div>
      <div class="d3-floats"></div>`;
    uiRoot.appendChild(this.root);
    this.canvas = this.root.querySelector(".d3-canvas")!;
    this.statusEl = this.root.querySelector(".d3-status")!;
    this.meBar = this.root.querySelector(".d3-bar-me")!;
    this.oppBar = this.root.querySelector(".d3-bar-opp")!;
    this.floatsEl = this.root.querySelector(".d3-floats")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => {
      if (this.phase === "over") { this.hide(); this.onRematch(); } else this.exit();
    });
    this.buildScene();
    this.bindStick();
  }

  // ---------- cena 3D ----------
  private buildScene(): void {
    this.scene.background = new THREE.Color(0x07070f);
    this.scene.fog = new THREE.Fog(0x07070f, 18, 44);
    this.scene.add(new THREE.AmbientLight(0x8895c8, 1.1));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(6, 16, 8);
    this.scene.add(dir);
    // chão (disco) + anel de energia
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(ARENA_R, 48),
      new THREE.MeshStandardMaterial({ color: 0x14172b, roughness: 0.95, metalness: 0.1 }),
    );
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(ARENA_R, 0.28, 8, 60),
      new THREE.MeshBasicMaterial({ color: 0x7c4dff }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.1;
    this.scene.add(ring);
    // grade de linhas radiais sutis
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const g = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.02, ARENA_R), new THREE.MeshBasicMaterial({ color: 0x2a2550 }));
      g.position.set(Math.sin(a) * ARENA_R / 2, 0.03, Math.cos(a) * ARENA_R / 2);
      g.rotation.y = a;
      this.scene.add(g);
    }
    // 10.9FG — feixe do ataque A DISTANCIA (arco/cajado)
    this.beam = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1, 6, 1, true), new THREE.MeshBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.scene.add(this.beam);
    this.meAv = this.buildAvatar(0x2be0ff);
    this.oppAv = this.buildAvatar(0xff5a6a);
    this.scene.add(this.meAv.group, this.oppAv.group);
    window.addEventListener("resize", () => { if (!this.root.hidden) this.resize(); }); // 10.9AW
  }

  /** 10.9AW — troca o avatar local pelo visual/arma reais do jogador. */
  private myLook: { v: string; e?: number; t?: number; f?: string } | null = null; // 10.9HK
  private oppSkinned = false;
  private rebuildMe(visualId: string, energy?: number, tier?: number, family?: string): void {
    this.scene.remove(this.meAv.group);
    this.meAv = this.buildAvatar(energy ?? 0x2be0ff, visualId, tier, family);
    this.scene.add(this.meAv.group);
  }

  private buildAvatar(color: number, visualId = "sword", tier?: number, family?: string): Avatar {
    const g = new THREE.Group();
    const mats: THREE.Material[] = [];
    // 10.9AW — CAÇADOR de verdade: capa, torso, ombreiras, cabeça com visor, cristal e a ARMA da classe
    const cloakMat = new THREE.MeshStandardMaterial({ color: 0x1b1630, emissive: color, emissiveIntensity: 0.18, roughness: 0.85, flatShading: true });
    const armorMat = new THREE.MeshStandardMaterial({ color: 0x3a3358, emissive: color, emissiveIntensity: 0.25, roughness: 0.45, metalness: 0.5, flatShading: true });
    const accent = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.9, roughness: 0.3, flatShading: true });
    mats.push(cloakMat, armorMat, accent);
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.58, 1.15, 10), cloakMat); // capa
    body.position.y = 0.62;
    body.castShadow = true;
    g.add(body);
    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.55, 0.42), armorMat);
    torso.position.y = 1.05;
    g.add(torso);
    for (const s of [-1, 1]) {
      const pad = new THREE.Mesh(new THREE.DodecahedronGeometry(0.2, 0), armorMat);
      pad.position.set(s * 0.4, 1.3, 0);
      pad.scale.set(1, 0.7, 1);
      g.add(pad);
    }
    const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.1, 0), accent);
    crystal.position.set(0, 1.05, 0.24);
    crystal.scale.set(0.7, 1.4, 0.7);
    g.add(crystal);
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.55, 8), cloakMat);
    hood.position.y = 1.72;
    g.add(hood);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 10, 8), armorMat);
    head.position.y = 1.5;
    g.add(head);
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.09, 0.14), accent);
    visor.position.set(0, 1.52, 0.22);
    g.add(visor);
    // arma real da classe na mão direita
    const weapon = new THREE.Group();
    const wm = buildWeaponMesh(visualId, { energy: color, glow: 1.2, tier, family }); // 10.9HG
    wm.scale.multiplyScalar(0.55);
    wm.rotation.z = -0.35;
    wm.position.set(0.42, 0.85, 0.15);
    weapon.add(wm);
    g.add(weapon);
    // arco de golpe (aparece ao atacar)
    const slash = new THREE.Mesh(
      new THREE.RingGeometry(ATTACK_RANGE * 0.5, ATTACK_RANGE, 20, 1, -0.9, 1.8),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false }),
    );
    slash.rotation.x = -Math.PI / 2;
    slash.position.y = 0.15;
    mats.push(slash.material as THREE.Material);
    g.add(slash);
    return { group: g, body, weapon, slash, mats };
  }

  // ---------- ciclo ----------
  open(myId: string, opp: LobbyPlayer, me: DuelFighter, myQ = 0): void {
    this.myId = myId;
    this.oppName = opp.name || "Rival";
    this.over = false;
    this.phase = "waiting";
    this.countdown = 3;
    this.waitTimer = 0;
    const ratio = me.power > 0 && opp.power > 0 ? me.power / opp.power : 1;
    this.myDmg = Math.max(2, Math.min(22, Math.round(7 * ratio)));
    // 10.9FG — ATRIBUTOS REAIS: vida, dano, alcance, velocidade de ataque e as 3 magias
    this.st = me.stats ?? null;
    this.myMax = this.st ? Math.round(this.st.maxHp * PVP_HP_MULT) : DUEL_HP;
    this.atkCd = this.st ? 1 / Math.max(0.3, this.st.aps) : ATTACK_CD;
    this.range = this.st ? Math.min(10, Math.max(2.6, this.st.range)) : ATTACK_RANGE;
    this.spellCd = [0, 0, 0];
    this.buffT = 0; this.buff = null;
    this.fields.length = 0; this.traps.length = 0; this.meteors.length = 0;
    this.buildSpellButtons();
    const iAmFirst = myId < opp.id;
    this.me = { x: 0, z: iAmFirst ? 6 : -6, hp: this.myMax, f: iAmFirst ? Math.PI : 0 };
    this.opp = { x: 0, z: iAmFirst ? -6 : 6, hp: DUEL_HP, maxHp: DUEL_HP, f: iAmFirst ? 0 : Math.PI };
    this.oppRender.set(this.opp.x, 0, this.opp.z);
    this.oppSeen = 0;
    this.atkTimer = 0;
    this.moveDir.set(0, 0);
    this.root.hidden = false;
    if (me.visualId) this.rebuildMe(me.visualId, me.energy, me.weaponTier, me.weaponFamily); // 10.9AW/HG — sua arma/cor/skin de verdade
    this.myLook = me.visualId ? { v: me.visualId, e: me.energy, t: me.weaponTier, f: me.weaponFamily } : null; // 10.9HK
    this.oppSkinned = false;
    (this.root.querySelector(".d3-bar-me .d3-name") as HTMLElement).textContent = me.name || "Você";
    (this.root.querySelector(".d3-bar-opp .d3-name") as HTMLElement).textContent = this.oppName;
    this.myCP = Math.round(me.power || 0); this.oppCP = Math.round(opp.power || 0); // 10.9FP
    (this.root.querySelector(".d3-bar-me .d3-cp") as HTMLElement).textContent = "⚔ " + fmtCP(this.myCP);
    (this.root.querySelector(".d3-bar-opp .d3-cp") as HTMLElement).textContent = "⚔ " + fmtCP(this.oppCP);
    this.root.querySelector('[data-a="close"]')!.textContent = "Sair";
    this.statusEl.textContent = `Aguardando ${this.oppName}…`;

    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
      this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    }
    this.resize();

    openDuelChannel(myId, opp.id, {
      onState: (s) => {
        this.opp = s;
        if (s.w && !this.oppSkinned) { // 10.9HK — o rival aparece com a arma/skin DELE
          this.oppSkinned = true;
          this.scene.remove(this.oppAv.group);
          this.oppAv = this.buildAvatar(s.w.e ?? 0xff5a6a, s.w.v, s.w.t, s.w.f);
          this.scene.add(this.oppAv.group);
        }
        // 10.9FF — a vida do rival chega no estado: se zerou e o "end" se perdeu, a vitoria vem daqui
        if (s.hp <= 0 && this.phase === "fighting" && !this.over) { this.finish(true); return; }
        this.oppSeen = performance.now();
        if (this.phase === "waiting") { this.phase = "countdown"; this.countdown = 3; this.statusEl.textContent = `Você  vs  ${this.oppName}`; }
      },
      onHit: (dmg) => this.takeDamage(dmg),
      onEnd: (loserId) => { if (!this.over) this.finish(loserId !== this.myId); },
      onOpponentLeft: () => { if (!this.over) this.finish(true, "O oponente saiu."); },
    }, [opp.q ?? 0, myQ].sort((a, b) => a - b).map((n) => n.toString(36)).join("")); // 10.9FF — canal novo por partida

    this.last = performance.now();
    this.loop(this.last);
  }

  hide(): void {
    this.root.hidden = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    closeDuelChannel();
  }

  private exit(): void {
    if (!this.over) sendDuelEnd(this.myId);
    this.hide();
    this.onExit();
  }

  private takeDamage(dmg: number): void {
    if (this.over) return;
    // 10.9FG — quem RECEBE aplica a propria defesa: armadura, reducao, bloqueio e buff
    if (this.st) {
      const raw = dmg;
      const arm = this.st.armor > 0 ? this.st.armor / (this.st.armor + 100) : 0;
      const blocked = Math.random() < Math.min(0.6, this.st.block);
      let d = raw * (1 - arm) * (1 - Math.min(0.8, this.st.dr)) * (1 - (this.buffT > 0 ? this.buff?.dr ?? 0 : 0)) * (blocked ? 0.5 : 1);
      d = Math.max(raw * PVP_MIN_TAKEN, d);
      dmg = Math.round(d);
      this.popRing(this.me.x, this.me.z, blocked ? 0xffffff : 0xff4d5a, 1.2);
    }
    this.popFloat(this.me.x, this.me.z, dmg, "taken", false);
    this.me.hp = Math.max(0, this.me.hp - dmg);
    if (this.me.hp <= 0) { sendDuelEnd(this.myId); this.finish(false); }
  }

  private finish(won: boolean, reason?: string): void {
    if (this.over) return;
    this.over = true;
    this.phase = "over";
    this.statusEl.innerHTML = won ? `<b class="d3-win">🏆 Você venceu!</b>${reason ? " " + reason : ""}` : `<b class="d3-lose">Você perdeu…</b>`;
    this.onResult(won);
    this.root.querySelector('[data-a="close"]')!.textContent = "🔁 Voltar ao lobby";
    closeDuelChannel();
  }

  // ---------- 10.9FG — combate real ----------
  /** golpe no rival: dano do Cacador x mult (critico, buff) — o rival aplica a defesa dele */
  private hitOpp(mult: number): void {
    if (!this.st) { sendDuelHit(this.myDmg); return; }
    const critCh = this.st.crit + (this.buffT > 0 ? this.buff?.critChance ?? 0 : 0);
    const crit = Math.random() < critCh;
    const dmg = this.st.dmg * mult * (crit ? Math.max(1, this.st.critMult) : 1) * (1 + (this.buffT > 0 ? this.buff?.dmgMult ?? 0 : 0));
    sendDuelHit(Math.round(dmg));
    if (this.st.lifeSteal > 0) this.me.hp = Math.min(this.myMax, this.me.hp + dmg * this.st.lifeSteal * 0.5); // aprox: antes da defesa do rival
    this.popRing(this.opp.x, this.opp.z, crit ? 0xffd24a : 0xffffff, crit ? 1.6 : 1);
    this.popFloat(this.opp.x, this.opp.z, Math.round(dmg), "dealt", crit);
  }
  private oppIn(x: number, z: number, r: number): boolean {
    return Math.hypot(this.opp.x - x, this.opp.z - z) <= r + 0.5;
  }
  private buildSpellButtons(): void {
    const box = this.root.querySelector<HTMLElement>(".d3-spells")!;
    const sp = this.st?.spells ?? [];
    box.innerHTML = sp.map((s, i) => `<button class="d3-sp" data-i="${i}" style="--c:#${s.color.toString(16).padStart(6, "0")}"><span>${s.emoji}</span><i class="d3-cd"></i></button>`).join("");
    this.spellBtns = [...box.querySelectorAll<HTMLButtonElement>(".d3-sp")];
    for (const b of this.spellBtns) {
      b.addEventListener("pointerdown", (e) => { e.stopPropagation(); e.preventDefault(); this.cast(Number(b.dataset.i)); });
    }
  }
  private cast(i: number): void {
    const s = this.st?.spells[i];
    if (!s || this.phase !== "fighting" || this.over || this.spellCd[i] > 0) return;
    this.spellCd[i] = s.cooldown;
    const dmg = (this.st?.dmg ?? 10) * s.dmgMult;
    // mira automatica no rival
    const ax = this.opp.x - this.me.x, az = this.opp.z - this.me.z, ad = Math.hypot(ax, az) || 1;
    const dx = ax / ad, dz = az / ad;
    this.me.f = Math.atan2(dx, dz);
    this.slashT = 0.22;
    const k = s.kind;
    if (k === "buff" && s.buff) {
      this.buff = s.buff; this.buffT = s.buff.seconds;
      if (s.buff.healFrac) this.me.hp = Math.min(this.myMax, this.me.hp + this.myMax * s.buff.healFrac);
      this.popRing(this.me.x, this.me.z, s.color, 2.6);
    } else if (k === "dash") {
      const reach = Math.min(s.dashRange ?? 7, ad + 1.5);
      const nx = this.me.x + dx * reach, nz = this.me.z + dz * reach, d = Math.hypot(nx, nz), lim = ARENA_R - 0.6;
      this.me.x = d > lim ? (nx / d) * lim : nx; this.me.z = d > lim ? (nz / d) * lim : nz;
      if (ad <= reach + s.radius) this.hitOpp(s.dmgMult);
      this.popRing(this.me.x, this.me.z, s.color, s.radius);
    } else if (k === "curse" || k === "whirl") {
      const fx = this.me.x + dx * (s.ahead ?? 0), fz = this.me.z + dz * (s.ahead ?? 0);
      const f = s.field ?? { seconds: 4, tickEvery: 0.5 };
      this.fields.push({ x: fx, z: fz, t: f.seconds, every: f.tickEvery, acc: 0, r: s.radius, dmg: s.dmgMult, follow: k === "whirl", drain: s.drainPct ?? 0 });
      this.popRing(fx, fz, s.color, s.radius);
    } else if (k === "trap" && s.trap) {
      this.traps.push({ x: this.me.x, z: this.me.z, t: s.trap.seconds, trig: s.trap.trigger, r: s.radius, dmg: s.dmgMult });
      this.popRing(this.me.x, this.me.z, s.color, s.trap.trigger);
    } else if (k === "meteor") {
      const reach = Math.min(s.meteorRange ?? 8, ad);
      const mx = this.me.x + dx * reach, mz = this.me.z + dz * reach;
      this.meteors.push({ x: mx, z: mz, t: s.meteorDelay ?? 0.9, r: s.radius, dmg: s.dmgMult });
      this.popRing(mx, mz, s.color, s.radius);
    } else if (k === "wave") {
      const len = s.waveLength ?? 12;
      if (ad <= len) this.hitOpp(s.dmgMult);
      for (let d = 1.5; d <= len; d += 2) this.popRing(this.me.x + dx * d, this.me.z + dz * d, s.color, s.radius * 0.8);
    } else if (k === "summon") {
      // servos nao cabem na arena: viram 3 golpes seguidos se o rival estiver perto
      if (ad <= 9) { for (let j = 0; j < 3; j++) window.setTimeout(() => { if (!this.over) this.hitOpp(Math.max(0.6, s.dmgMult)); }, j * 350); }
      this.popRing(this.me.x, this.me.z, s.color, 2.2);
    } else {
      // slam / nova / volley / chain: acerta se o rival estiver no alcance da magia
      const reach = k === "volley" || k === "chain" ? Math.max(s.radius, 10) : s.radius;
      if (ad <= reach + 0.5) this.hitOpp(s.dmgMult);
      if (k === "volley" || k === "chain") { this.beamT = 0.25; this.popRing(this.opp.x, this.opp.z, s.color, 2.2); }
      else this.popRing(this.me.x, this.me.z, s.color, s.radius);
    }
    void dmg;
  }
  private updateSpellEffects(dt: number): void {
    for (let i = this.fields.length - 1; i >= 0; i--) {
      const f = this.fields[i];
      f.t -= dt; f.acc += dt;
      if (f.follow) { f.x = this.me.x; f.z = this.me.z; }
      if (f.acc >= f.every) {
        f.acc = 0;
        if (this.oppIn(f.x, f.z, f.r)) {
          this.hitOpp(f.dmg);
          if (f.drain > 0) this.me.hp = Math.min(this.myMax, this.me.hp + this.myMax * f.drain);
        }
      }
      if (f.t <= 0) this.fields.splice(i, 1);
    }
    for (let i = this.traps.length - 1; i >= 0; i--) {
      const t = this.traps[i];
      t.t -= dt;
      if (this.oppIn(t.x, t.z, t.trig)) { this.hitOpp(t.dmg); this.popRing(t.x, t.z, 0xff9a3c, t.r); this.traps.splice(i, 1); }
      else if (t.t <= 0) this.traps.splice(i, 1);
    }
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const m = this.meteors[i];
      m.t -= dt;
      if (m.t <= 0) { if (this.oppIn(m.x, m.z, m.r)) this.hitOpp(m.dmg); this.popRing(m.x, m.z, 0xd15bff, m.r * 1.3); this.meteors.splice(i, 1); }
    }
  }
  /** 10.9FP — numero de dano flutuante (causado = claro/amarelo no crit; sofrido = vermelho). */
  private popFloat(x: number, z: number, amount: number, kind: "dealt" | "taken", crit: boolean): void {
    if (amount <= 0 || !this.renderer) return;
    const v = new THREE.Vector3(x, 1.9, z).project(this.camera);
    if (v.z > 1) return;
    const el = document.createElement("div");
    el.className = `d3-float ${kind}${crit ? " crit" : ""}`;
    el.textContent = (kind === "taken" ? "-" : "") + fmtCP(Math.round(amount)) + (crit ? "!" : "");
    el.style.left = `${(v.x * 0.5 + 0.5) * this.root.clientWidth}px`;
    el.style.top = `${(-v.y * 0.5 + 0.5) * this.root.clientHeight}px`;
    this.floatsEl.appendChild(el);
    window.setTimeout(() => el.remove(), 850);
  }
  /** anel que cresce e some (golpe, magia, bloqueio) */
  private popRing(x: number, z: number, color: number, r: number): void {
    const m = new THREE.Mesh(new THREE.RingGeometry(0.7, 1, 32), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }));
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, 0.14, z);
    this.scene.add(m);
    this.fx.push({ m, t: 0, dur: 0.45, grow: Math.max(1, r) });
  }
  private updateFx(dt: number): void {
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      f.t += dt;
      const k = f.t / f.dur;
      const sc = 0.4 + k * f.grow;
      f.m.scale.set(sc, sc, sc);
      (f.m.material as THREE.MeshBasicMaterial).opacity = 0.85 * (1 - k);
      if (f.t >= f.dur) { this.scene.remove(f.m); f.m.geometry.dispose(); (f.m.material as THREE.Material).dispose(); this.fx.splice(i, 1); }
    }
    // feixe do ataque a distancia
    const bm = this.beam.material as THREE.MeshBasicMaterial;
    if (this.beamT > 0) {
      this.beamT -= dt;
      const ax = this.me.x, az = this.me.z, bx = this.oppRender.x, bz = this.oppRender.z;
      const len = Math.hypot(bx - ax, bz - az);
      this.beam.position.set((ax + bx) / 2, 1.1, (az + bz) / 2);
      this.beam.scale.set(1, len, 1);
      this.beam.rotation.set(Math.PI / 2, 0, 0);
      this.beam.rotation.y = 0;
      this.beam.lookAt(bx, 1.1, bz);
      this.beam.rotateX(Math.PI / 2);
      bm.opacity = 0.9;
    } else bm.opacity = 0;
    // recarga nos botoes
    const sp = this.st?.spells ?? [];
    this.spellBtns.forEach((b, i) => {
      const cd = sp[i] ? this.spellCd[i] / sp[i].cooldown : 0;
      (b.querySelector(".d3-cd") as HTMLElement).style.transform = `scaleY(${cd})`;
      b.classList.toggle("ready", cd <= 0 && this.phase === "fighting");
    });
  }

  // ---------- input: joystick ----------
  private bindStick(): void {
    const stick = this.root.querySelector(".d3-stick") as HTMLElement;
    const knob = this.root.querySelector(".d3-knob") as HTMLElement;
    let id = -1;
    let cx = 0, cy = 0;
    const R = 46;
    const start = (e: PointerEvent): void => {
      id = e.pointerId;
      const r = stick.getBoundingClientRect();
      cx = r.left + r.width / 2; cy = r.top + r.height / 2;
      move(e);
    };
    const move = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      let dx = e.clientX - cx, dy = e.clientY - cy;
      const d = Math.hypot(dx, dy) || 1;
      const cl = Math.min(1, d / R);
      dx = (dx / d); dy = (dy / d);
      knob.style.transform = `translate(${dx * R * cl}px, ${dy * R * cl}px)`;
      this.moveDir.set(dx * cl, dy * cl);
    };
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      id = -1; this.moveDir.set(0, 0);
      knob.style.transform = "translate(0,0)";
    };
    // o joystick pode ser arrastado a partir de qualquer lugar da metade de baixo do canvas
    this.root.addEventListener("pointerdown", (e) => { if ((e.target as HTMLElement).closest(".btn, .d3-sp")) return; start(e); });
    this.root.addEventListener("pointermove", move);
    this.root.addEventListener("pointerup", end);
    this.root.addEventListener("pointercancel", end);
  }

  private resize(): void {
    const w = this.root.clientWidth || window.innerWidth;
    const h = this.root.clientHeight || window.innerHeight;
    this.renderer?.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ---------- loop ----------
  private loop = (now: number): void => {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    if (!this.over) this.step(dt);
    this.render(dt);
    this.raf = requestAnimationFrame(this.loop);
  };

  private moveMe(dt: number): void {
    if (this.moveDir.lengthSq() < 0.0004) return;
    const nx = this.me.x + this.moveDir.x * MOVE_SPEED * dt;
    const nz = this.me.z + this.moveDir.y * MOVE_SPEED * dt;
    // clamp ao anel
    const d = Math.hypot(nx, nz);
    const lim = ARENA_R - 0.6;
    this.me.x = d > lim ? (nx / d) * lim : nx;
    this.me.z = d > lim ? (nz / d) * lim : nz;
    this.me.f = Math.atan2(this.moveDir.x, this.moveDir.y);
  }

  private step(dt: number): void {
    this.netTimer -= dt;
    if (this.netTimer <= 0) {
      this.netTimer = 0.08;
      sendDuelState({ id: this.myId, x: this.me.x, z: this.me.z, hp: this.me.hp, maxHp: this.myMax, f: this.me.f, a: this.slashT > 0.12, w: this.myLook ?? undefined });
    }
    // interpola oponente
    const t = Math.min(1, dt * 12);
    this.oppRender.x += (this.opp.x - this.oppRender.x) * t;
    this.oppRender.z += (this.opp.z - this.oppRender.z) * t;
    if (this.slashT > 0) this.slashT -= dt;

    if (this.phase === "waiting") {
      this.waitTimer += dt;
      // 10.9FF — rival nao apareceu: volta pro LOBBY avisando (antes jogava pro mapa sem dizer nada)
      if (this.waitTimer > 12) { this.hide(); this.onRematch("O oponente não respondeu. Entre na fila de novo."); }
      return;
    }
    if (this.phase === "countdown") {
      this.moveMe(dt);
      this.countdown -= dt;
      if (this.countdown <= 0) this.phase = "fighting";
      return;
    }
    // fighting
    this.moveMe(dt);
    this.atkTimer -= dt;
    for (let i = 0; i < 3; i++) this.spellCd[i] = Math.max(0, this.spellCd[i] - dt);
    if (this.buffT > 0) { this.buffT -= dt; if (this.buffT <= 0) this.buff = null; }
    this.updateSpellEffects(dt);
    const distOpp = Math.hypot(this.opp.x - this.me.x, this.opp.z - this.me.z);
    if (distOpp <= this.range && this.atkTimer <= 0) {
      const spd = 1 + (this.buffT > 0 ? this.buff?.atkSpeedMult ?? 0 : 0);
      this.atkTimer = this.atkCd / spd;
      this.slashT = 0.22;
      // encara o oponente ao golpear
      this.me.f = Math.atan2(this.opp.x - this.me.x, this.opp.z - this.me.z);
      if (this.range > 4) this.beamT = 0.12; // arco/cajado: feixe ate o rival
      this.hitOpp(1);
    }
    if (performance.now() - this.oppSeen > 6000) this.finish(true, "O oponente caiu.");
  }

  private render(dt: number): void {
    if (!this.renderer) return;
    // posiciona avatares
    this.meAv.group.position.set(this.me.x, 0, this.me.z);
    this.meAv.group.rotation.y = this.me.f;
    this.oppAv.group.position.set(this.oppRender.x, 0, this.oppRender.z);
    this.oppAv.group.rotation.y = this.opp.f ?? 0;
    // animação de golpe (arco pulsa)
    (this.meAv.slash.material as THREE.MeshBasicMaterial).opacity = Math.max(0, this.slashT / 0.22) * 0.8;
    const oppAtk = this.opp.a ? 0.7 : 0;
    (this.oppAv.slash.material as THREE.MeshBasicMaterial).opacity = oppAtk;

    // 10.9AW — bob de caminhada nos dois avatares (peso) + golpe inclina o corpo
    const walkMe = this.moveDir.lengthSq() > 0.0004;
    this.meAv.group.position.y = walkMe ? Math.abs(Math.sin(performance.now() * 0.012)) * 0.08 : 0;
    this.meAv.group.rotation.x = this.slashT > 0 ? 0.25 : walkMe ? 0.08 : 0;
    this.oppAv.group.rotation.x = this.opp.a ? 0.25 : 0;
    // câmera ALTA e afastada, olhando à frente do jogador: o avatar fica no terço inferior da tela,
    // livre do joystick, e o oponente sempre em quadro
    // 10.9AW — câmera no PONTO MÉDIO dos dois (altura cresce com a distância): os dois ficam no
    // centro da tela, ninguém embaixo do joystick nem cortado na borda.
    const mx = (this.me.x + this.oppRender.x) / 2, mz = (this.me.z + this.oppRender.z) / 2;
    const dist = Math.hypot(this.me.x - this.oppRender.x, this.me.z - this.oppRender.z);
    const h = Math.min(34, Math.max(18, 9 + dist * 1.5));
    const camTarget = new THREE.Vector3(mx, 0.5, mz + 1.5); // alvo levemente pro lado do jogador (fica acima do joystick)
    const camPos = new THREE.Vector3(mx, h, mz + h * 0.22); // quase top-down: os dois simétricos no centro
    this.camera.position.lerp(camPos, Math.min(1, dt * 6));
    this.camera.lookAt(camTarget);

    this.updateFx(dt); // 10.9FG
    this.renderer.render(this.scene, this.camera);
    this.projectBars();
  }

  /** Posiciona as barras de vida HTML sobre as cabeças (projeção 3D→tela). */
  private projectBars(): void {
    this.placeBar(this.meBar, this.me.x, this.me.z, this.me.hp / this.myMax);
    this.placeBar(this.oppBar, this.oppRender.x, this.oppRender.z, this.opp.hp / (this.opp.maxHp || DUEL_HP));
  }
  private placeBar(el: HTMLElement, x: number, z: number, hpFrac: number): void {
    const v = new THREE.Vector3(x, 2.1, z).project(this.camera);
    const w = this.root.clientWidth, h = this.root.clientHeight;
    const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
    const vis = v.z < 1;
    el.style.display = vis ? "flex" : "none";
    el.style.left = `${sx}px`;
    el.style.top = `${sy}px`;
    const bar = el.querySelector(".d3-hpwrap i") as HTMLElement;
    bar.style.transform = `scaleX(${Math.max(0, Math.min(1, hpFrac))})`;
  }
}

function fmtCP(n: number): string { return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "K" : String(n); } // 10.9FP
let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .d3-overlay{position:fixed;inset:0;z-index:40;background:#07070f;overflow:hidden;touch-action:none}
  .d3-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
  .d3-hud{position:absolute;inset:0;pointer-events:none}
  .d3-top{position:absolute;top:calc(env(safe-area-inset-top,0px) + 10px);left:0;right:0;display:flex;align-items:center;justify-content:space-between;padding:0 14px;gap:10px}
  .d3-status{font-size:15px;color:#eaf1ff;font-weight:800;text-shadow:0 2px 6px #000}
  .d3-win{color:#8affc0;font-size:19px}
  .d3-lose{color:#ff9a9a;font-size:19px}
  .d3-x{pointer-events:auto;padding:8px 14px}
  .d3-bar{position:absolute;transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;gap:2px;pointer-events:none;will-change:left,top}
  .d3-name{font-size:11px;font-weight:800;color:#eaf1ff;text-shadow:0 1px 3px #000;white-space:nowrap}
  .d3-cp{font-size:10px;font-weight:800;color:#ffd76a;text-shadow:0 1px 3px #000}
  .d3-floats{position:absolute;inset:0;pointer-events:none;overflow:hidden}
  .d3-float{position:absolute;transform:translate(-50%,-50%);font:900 20px system-ui,sans-serif;text-shadow:0 2px 4px #000,0 0 6px #000;animation:d3FloatUp .85s ease-out forwards;will-change:transform,opacity}
  .d3-float.dealt{color:#fff}
  .d3-float.taken{color:#ff5a5a}
  .d3-float.crit{font-size:27px;color:#ffd24a}
  @keyframes d3FloatUp{0%{opacity:0;transform:translate(-50%,-30%) scale(.7)}20%{opacity:1;transform:translate(-50%,-60%) scale(1.1)}100%{opacity:0;transform:translate(-50%,-150%) scale(1)}}
  .d3-hpwrap{width:56px;height:7px;border-radius:6px;background:rgba(0,0,0,.6);overflow:hidden;border:1px solid rgba(255,255,255,.15)}
  .d3-hpwrap i{display:block;width:100%;height:100%;transform-origin:left;background:linear-gradient(90deg,#38f5a0,#8affc0)}
  .d3-bar-opp .d3-hpwrap i{background:linear-gradient(90deg,#ff5a6a,#ff9a9a)}
  .d3-stick{position:absolute;left:50%;transform:translateX(-50%);bottom:calc(env(safe-area-inset-bottom,0px) + 30px);
    width:120px;height:120px;border-radius:50%;background:radial-gradient(circle at 50% 45%, rgba(124,77,255,.14), rgba(124,77,255,.04) 72%);
    border:3px solid rgba(160,140,255,.4);pointer-events:auto;touch-action:none}
  .d3-spells{position:absolute;right:calc(env(safe-area-inset-right,0px) + 14px);bottom:calc(env(safe-area-inset-bottom,0px) + 34px);display:flex;flex-direction:column;gap:10px;pointer-events:auto}
  .d3-sp{position:relative;width:62px;height:62px;border-radius:50%;border:3px solid var(--c);background:rgba(10,12,26,.8);font-size:26px;overflow:hidden;opacity:.6;touch-action:none}
  .d3-sp.ready{opacity:1;box-shadow:0 0 14px var(--c)}
  .d3-sp .d3-cd{position:absolute;left:0;right:0;bottom:0;height:100%;background:rgba(0,0,0,.6);transform-origin:bottom;transform:scaleY(0);pointer-events:none}
  .d3-knob{position:absolute;left:50%;top:50%;width:54px;height:54px;margin:-27px 0 0 -27px;border-radius:50%;
    border:2px solid rgba(214,198,255,.9);background:radial-gradient(circle at 42% 34%, rgba(196,176,255,.9), rgba(124,77,255,.62) 78%)}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
